import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { pdf, Document } from "@react-pdf/renderer";
import { Select, Button } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import "../../styles/pages/facultyProfilePage.scss";
import FacultyProfileCard from "../../components/FacultyProfile/FacultyProfileCard";
import WorkloadSection from "../../components/FacultyProfile/WorkloadSection";
import TeachingLoadTabs from "../../components/FacultyProfile/TeachingLoadTabs";
import ConsultationHoursSection from "../../components/FacultyProfile/ConsultationHoursSection";
import TermDetailsSection from "../../components/FacultyProfile/TermDetailsSection";
import FacultyProfileModal from "../../components/FacultyProfile/FacultyProfileModal";
import GeneratePortfolioModal from "../../components/FacultyProfile/GeneratePortfolioModal";
import TermSelect from "../../components/TermSelect";
import PortfolioFormat from "../../generatePdf/documents/PortfolioFormat";
import { sortItemsByTermDesc, sortTermsAsc } from "../../utils/generalUtils";
import { facultyProfileConfig } from "../../config/facultyProfileConfig";
import useAcademicTerms from "../../hooks/useAcademicTerms";

export default function FacultyProfilePage() {
  const { details } = useSelector((state) => state.accountDetailSlice);

  const userName = details?.first_name
    ? `${details.first_name} ${details.last_name}`
    : details?.email;

  const baseUrl =
    process.env.REACT_APP_API_URL?.replace(/\/api$/, "") ??
    "http://localhost:8000";
  const profilePhotoUrl = details?.profile_photo
    ? `${baseUrl}${details.profile_photo}`
    : null;

  const config = facultyProfileConfig;
  const termOptions = useAcademicTerms();

  // -------------------------
  // Filters
  // -------------------------

  const [selectedTerms, setSelectedTerms] = useState([]);
  const [selectedTerm, setSelectedTerm] = useState();

  useEffect(() => {
    if (termOptions.length > 0 && !selectedTerm) {
      setSelectedTerm(termOptions[0].value);
    }
  }, [termOptions, selectedTerm]);

  const filterByTerms = (data) => {
    return selectedTerms.length === 0
      ? data
      : data.filter((item) => selectedTerms.includes(item.academic_term));
  };

  // -------------------------
  // Data Fetching
  // -------------------------

  const [teachingLoadUpouData, setTeachingLoadUpouData] = useState([]);
  const [teachingLoadOtherUpData, setTeachingLoadOtherUpData] = useState([]);
  const [teachingLoadOutsideUpData, setTeachingLoadOutsideUpData] = useState(
    [],
  );
  const [researchData, setResearchData] = useState([]);
  const [administrativeWorkData, setAdministrativeWorkData] = useState([]);
  const [tutorialWorkData, setTutorialWorkData] = useState([]);
  const [extensionServiceData, setExtensionServiceData] = useState([]);
  const [studyLoadData, setStudyLoadData] = useState([]);
  const [limitedPracticeData, setLimitedPracticeData] = useState([]);
  const [recipientOrNomineeData, setRecipientOrNomineeData] = useState([]);
  const [consultationHoursData, setConsultationHoursData] = useState([]);

  const endpoints = [
    { endpoint: "research", setter: setResearchData },
    { endpoint: "teaching-load-upou", setter: setTeachingLoadUpouData },
    { endpoint: "teaching-load-other-up", setter: setTeachingLoadOtherUpData },
    {
      endpoint: "teaching-load-outside-up",
      setter: setTeachingLoadOutsideUpData,
    },
    { endpoint: "administrative-work", setter: setAdministrativeWorkData },
    { endpoint: "tutorial-work", setter: setTutorialWorkData },
    { endpoint: "extension-service", setter: setExtensionServiceData },
    { endpoint: "study-load", setter: setStudyLoadData },
    {
      endpoint: "limited-practice-of-profession",
      setter: setLimitedPracticeData,
    },
    { endpoint: "recipient-or-nominee", setter: setRecipientOrNomineeData },
    { endpoint: "consultation-hours", setter: setConsultationHoursData },
  ];

  const fetchData = async (endpoint, setter) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/${endpoint}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("api_token")}`,
        },
      });
      const data = await res.json();
      setter(data);
    } catch (err) {
      console.error(`Error fetching ${endpoint}:`, err);
    }
  };

  useEffect(() => {
    endpoints.forEach(({ endpoint, setter }) => {
      fetchData(endpoint, setter);
    });
  }, []);

  const selectedStudyLoad = studyLoadData?.find(
    (item) => item.academic_term === selectedTerm,
  );
  const selectedLimitedPractice = limitedPracticeData?.find(
    (item) => item.academic_term === selectedTerm,
  );
  const selectedRecipientOrNominee = recipientOrNomineeData?.find(
    (item) => item.academic_term === selectedTerm,
  );

  // -------------------------
  // Teaching Load Tabs
  // -------------------------

  const [activeTeachingTab, setActiveTeachingTab] = useState("upou");

  const teachingLoadMap = {
    upou: {
      endpoint: "teaching-load-upou",
      data: teachingLoadUpouData,
      setter: setTeachingLoadUpouData,
    },
    otherUp: {
      endpoint: "teaching-load-other-up",
      data: teachingLoadOtherUpData,
      setter: setTeachingLoadOtherUpData,
    },
    outsideUp: {
      endpoint: "teaching-load-outside-up",
      data: teachingLoadOutsideUpData,
      setter: setTeachingLoadOutsideUpData,
    },
  };

  const getTeachingLoadData = () => {
    const { data } = teachingLoadMap[activeTeachingTab];
    return filterByTerms(sortItemsByTermDesc(data));
  };

  // -------------------------
  // CRUD Modal State
  // -------------------------

  const [modalState, setModalState] = useState({ open: false });

  const openModal = ({
    mode,
    showDelete,
    title,
    config,
    initialEditValues,
    onSubmit,
    onDelete,
  }) => {
    setModalState({
      open: true,
      mode,
      showDelete,
      title,
      config,
      initialEditValues,
      onSubmit,
      onDelete,
    });
  };

  const closeModal = () => {
    setModalState({ open: false });
  };

  // --------------------------------
  // Portfolio Modal State
  // --------------------------------

  const [portfolioModalState, setPortfolioModalState] = useState({
    open: false,
  });

  const openPortfolioModal = ({ onSubmit }) => {
    setPortfolioModalState({
      open: true,
      onSubmit,
    });
  };

  const closePortfolioModal = () => {
    setPortfolioModalState({ open: false });
  };

  // -------------------------
  // Sorting
  // -------------------------

  // Sort selected terms label
  const handleTermChange = (values) => {
    const sortedValues = sortTermsAsc(values);
    setSelectedTerms(sortedValues);
  };

  // Sort consultation hours
  const dayOrder = {
    Sunday: 0,
    Monday: 1,
    Tuesday: 2,
    Wednesday: 3,
    Thursday: 4,
    Friday: 5,
    Saturday: 6,
  };
  const sortByDay = (data) => {
    return [...data].sort((a, b) => {
      return dayOrder[a.day] - dayOrder[b.day];
    });
  };

  // -------------------------
  // CRUD Handlers
  // -------------------------

  const handleAdd = async (endpoint, values, setter, currentData) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("api_token")}`,
        },
        body: JSON.stringify(values),
      });

      const newItem = await res.json();

      setter([...currentData, newItem]);
    } catch (err) {
      console.error("Add error:", err);
    }
  };

  const handleEdit = async (endpoint, id, values, setter, currentData) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/${endpoint}/${id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("api_token")}`,
          },
          body: JSON.stringify(values),
        },
      );

      const updatedItem = await res.json();

      setter(currentData.map((item) => (item.id === id ? updatedItem : item)));
    } catch (err) {
      console.error("Edit error:", err);
    }
  };

  const handleDelete = async (endpoint, id, setter, currentData) => {
    try {
      await fetch(`${process.env.REACT_APP_API_URL}/${endpoint}/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("api_token")}`,
        },
      });

      setter(currentData.filter((item) => item.id !== id));
    } catch (err) {
      console.error("Delete error:", err);
    }
  };

  // ----------------------------
  // Generate Portfolio Handler
  // ----------------------------

  const handleGeneratePortfolio = async ({ academic_terms, sections }) => {
    const selectedTerms = academic_terms || [];

    const filterTerms = (items) => {
      if (!selectedTerms.length) return items;

      return items.filter((item) => selectedTerms.includes(item.academic_term));
    };

    const sectionMap = {
      teaching_load_upou: {
        title: "Teaching Load In UPOU",
        items: filterTerms(teachingLoadUpouData),
        fields: config.teachingLoad.tabs.upou.fields,
      },

      teaching_load_other_up: {
        title: "Teaching Load In Other UP Units",
        items: filterTerms(teachingLoadOtherUpData),
        fields: config.teachingLoad.tabs.otherUp.fields,
      },

      teaching_load_outside_up: {
        title: "Teaching Load Outside UP",
        items: filterTerms(teachingLoadOutsideUpData),
        fields: config.teachingLoad.tabs.outsideUp.fields,
      },

      research: {
        title: config.research.title,
        items: filterTerms(researchData),
        fields: config.research.fields,
      },

      administrative_work: {
        title: config.administrativeWork.title,
        items: filterTerms(administrativeWorkData),
        fields: config.administrativeWork.fields,
      },

      tutorial_work: {
        title: config.tutorialWork.title,
        items: filterTerms(tutorialWorkData),
        fields: config.tutorialWork.fields,
      },

      extension_service: {
        title: config.extensionService.title,
        items: filterTerms(extensionServiceData),
        fields: config.extensionService.fields,
      },
    };

    const selectedSections = sections.map((key) => sectionMap[key]);

    const blob = await pdf(
      <Document>
        <PortfolioFormat
          contents={{
            facultyName: userName,
            selectedTerms,
            sections: selectedSections,
          }}
        />
        ,
      </Document>,
    ).toBlob();

    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
  };

  return (
    <div className="faculty-profile-page">
      {/* ========================= */}
      {/* Faculty Profile Card */}
      {/* ========================= */}

      <FacultyProfileCard name={userName} image={profilePhotoUrl} />

      {/* ========================= */}
      {/* WORKLOADS */}
      {/* ========================= */}

      <div className="section-title">Workloads</div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 10,
        }}
      >
        <Select
          mode="multiple"
          allowClear
          placeholder="Filter by academic term"
          value={selectedTerms}
          onChange={handleTermChange}
          options={termOptions}
          maxTagCount={2}
          style={{ width: "670px" }}
        />

        <Button
          style={{ background: "#008767", width: "fit-content" }}
          icon={<DownloadOutlined />}
          type="primary"
          onClick={() =>
            openPortfolioModal({
              onSubmit: handleGeneratePortfolio,
            })
          }
        >
          Generate Portfolio
        </Button>
      </div>

      {/* ========================= */}
      {/* Teaching Load */}
      {/* ========================= */}

      <WorkloadSection
        title={config.teachingLoad.title}
        selectedTerms={selectedTerms}
        portfolioProps={{
          title: `${config.teachingLoad.title} — ${config.teachingLoad.tabs[activeTeachingTab].label}`,
          items: getTeachingLoadData(),
          fields: config.teachingLoad.tabs[activeTeachingTab].fields,
          selectedTerms,
          facultyName: userName,
        }}
        onAdd={() => {
          const tab = config.teachingLoad.tabs[activeTeachingTab];
          const { endpoint, data, setter } = teachingLoadMap[activeTeachingTab];

          openModal({
            mode: "add",
            showDelete: false,
            title: `${config.teachingLoad.title} — ${tab.label}`,
            config: tab,
            onSubmit: (values) => handleAdd(endpoint, values, setter, data),
          });
        }}
      >
        <TeachingLoadTabs
          activeTab={activeTeachingTab}
          onTabChange={setActiveTeachingTab}
          selectedTerms={selectedTerms}
          onEdit={(rowData) => {
            const tab = config.teachingLoad.tabs[activeTeachingTab];
            const { endpoint, data, setter } =
              teachingLoadMap[activeTeachingTab];

            openModal({
              mode: "edit",
              showDelete: true,
              title: `${config.teachingLoad.title} — ${tab.label}`,
              config: tab,
              initialEditValues: rowData,
              onSubmit: (values) =>
                handleEdit(endpoint, rowData.id, values, setter, data),
              onDelete: () => handleDelete(endpoint, rowData.id, setter, data),
            });
          }}
          teachingLoadUpouData={sortItemsByTermDesc(teachingLoadUpouData)}
          teachingLoadOtherUpData={sortItemsByTermDesc(teachingLoadOtherUpData)}
          teachingLoadOutsideUpData={sortItemsByTermDesc(
            teachingLoadOutsideUpData,
          )}
        />
      </WorkloadSection>

      {/* ========================= */}
      {/* Research */}
      {/* ========================= */}

      <WorkloadSection
        title={config.research.title}
        columns={config.research.columns}
        data={sortItemsByTermDesc(researchData)}
        selectedTerms={selectedTerms}
        portfolioProps={{
          title: config.research.title,
          items: filterByTerms(sortItemsByTermDesc(researchData)),
          fields: config.research.fields,
          selectedTerms,
          facultyName: userName,
        }}
        onAdd={() =>
          openModal({
            mode: "add",
            showDelete: false,
            title: "Research, Textbook Writing, Course Dev., & Creative Work",
            config: config.research,
            onSubmit: (values) =>
              handleAdd("research", values, setResearchData, researchData),
          })
        }
        onEdit={(rowData) =>
          openModal({
            mode: "edit",
            showDelete: true,
            title: "Research, Textbook Writing, Course Dev., & Creative Work",
            config: config.research,
            initialEditValues: rowData,
            onSubmit: (values) =>
              handleEdit(
                "research",
                rowData.id,
                values,
                setResearchData,
                researchData,
              ),
            onDelete: () =>
              handleDelete(
                "research",
                rowData.id,
                setResearchData,
                researchData,
              ),
          })
        }
      />

      {/* ========================= */}
      {/* Administrative Work */}
      {/* ========================= */}

      <WorkloadSection
        title={config.administrativeWork.title}
        columns={config.administrativeWork.columns}
        data={sortItemsByTermDesc(administrativeWorkData)}
        selectedTerms={selectedTerms}
        portfolioProps={{
          title: config.administrativeWork.title,
          items: filterByTerms(sortItemsByTermDesc(administrativeWorkData)),
          fields: config.administrativeWork.fields,
          selectedTerms,
          facultyName: userName,
        }}
        onAdd={() =>
          openModal({
            mode: "add",
            showDelete: false,
            title: config.administrativeWork.title,
            config: config.administrativeWork,
            onSubmit: (values) =>
              handleAdd(
                "administrative-work",
                values,
                setAdministrativeWorkData,
                administrativeWorkData,
              ),
          })
        }
        onEdit={(rowData) =>
          openModal({
            mode: "edit",
            showDelete: true,
            title: config.administrativeWork.title,
            config: config.administrativeWork,
            initialEditValues: rowData,
            onSubmit: (values) =>
              handleEdit(
                "administrative-work",
                rowData.id,
                values,
                setAdministrativeWorkData,
                administrativeWorkData,
              ),
            onDelete: () =>
              handleDelete(
                "administrative-work",
                rowData.id,
                setAdministrativeWorkData,
                administrativeWorkData,
              ),
          })
        }
      />

      {/* ========================= */}
      {/* Tutorial Work */}
      {/* ========================= */}

      <WorkloadSection
        title={config.tutorialWork.title}
        columns={config.tutorialWork.columns}
        data={sortItemsByTermDesc(tutorialWorkData)}
        selectedTerms={selectedTerms}
        portfolioProps={{
          title: config.tutorialWork.title,
          items: filterByTerms(sortItemsByTermDesc(tutorialWorkData)),
          fields: config.tutorialWork.fields,
          selectedTerms,
          facultyName: userName,
        }}
        onAdd={() =>
          openModal({
            mode: "add",
            showDelete: false,
            title: config.tutorialWork.title,
            config: config.tutorialWork,
            onSubmit: (values) =>
              handleAdd(
                "tutorial-work",
                values,
                setTutorialWorkData,
                tutorialWorkData,
              ),
          })
        }
        onEdit={(rowData) =>
          openModal({
            mode: "edit",
            showDelete: true,
            title: config.tutorialWork.title,
            config: config.tutorialWork,
            initialEditValues: rowData,
            onSubmit: (values) =>
              handleEdit(
                "tutorial-work",
                rowData.id,
                values,
                setTutorialWorkData,
                tutorialWorkData,
              ),
            onDelete: () =>
              handleDelete(
                "tutorial-work",
                rowData.id,
                setTutorialWorkData,
                tutorialWorkData,
              ),
          })
        }
      />

      {/* ========================= */}
      {/* Extension */}
      {/* ========================= */}

      <WorkloadSection
        title={config.extensionService.title}
        columns={config.extensionService.columns}
        data={sortItemsByTermDesc(extensionServiceData)}
        selectedTerms={selectedTerms}
        portfolioProps={{
          title: config.extensionService.title,
          items: filterByTerms(sortItemsByTermDesc(extensionServiceData)),
          fields: config.extensionService.fields,
          selectedTerms,
          facultyName: userName,
        }}
        onAdd={() =>
          openModal({
            mode: "add",
            showDelete: false,
            title: config.extensionService.title,
            config: config.extensionService,
            onSubmit: (values) =>
              handleAdd(
                "extension-service",
                values,
                setExtensionServiceData,
                extensionServiceData,
              ),
          })
        }
        onEdit={(rowData) =>
          openModal({
            mode: "edit",
            showDelete: true,
            title: config.extensionService.title,
            config: config.extensionService,
            initialEditValues: rowData,
            onSubmit: (values) =>
              handleEdit(
                "extension-service",
                rowData.id,
                values,
                setExtensionServiceData,
                extensionServiceData,
              ),
            onDelete: () =>
              handleDelete(
                "extension-service",
                rowData.id,
                setExtensionServiceData,
                extensionServiceData,
              ),
          })
        }
      />

      {/* ========================= */}
      {/* OTHER INFORMATION */}
      {/* ========================= */}

      <div className="section-title">Other Information</div>

      <TermSelect
        value={selectedTerm}
        onChange={setSelectedTerm}
        width="310px"
      />

      {/* Term Detail Sections */}

      <TermDetailsSection
        title={config.studyLoad.title}
        fields={config.studyLoad.fields}
        data={selectedStudyLoad}
        selectedTerm={selectedTerm}
        onEdit={() => {
          openModal({
            mode: "edit",
            showDelete: false,
            title: (
              <div style={{ display: "flex", flexDirection: "column" }}>
                {config.studyLoad.title}
                <span>{selectedTerm}</span>
              </div>
            ),
            config: config.studyLoad,
            initialEditValues: selectedStudyLoad,
            onSubmit: (values) => {
              if (selectedStudyLoad) {
                handleEdit(
                  "study-load",
                  selectedStudyLoad.id,
                  values,
                  setStudyLoadData,
                  studyLoadData,
                );
              } else {
                handleAdd(
                  "study-load",
                  {
                    ...values,
                    academic_term: selectedTerm,
                  },
                  setStudyLoadData,
                  studyLoadData,
                );
              }
            },
          });
        }}
      />

      <TermDetailsSection
        title={config.limitedPractice.title}
        fields={config.limitedPractice.fields}
        data={selectedLimitedPractice}
        selectedTerm={selectedTerm}
        onEdit={() => {
          openModal({
            mode: "edit",
            showDelete: false,
            title: (
              <div style={{ display: "flex", flexDirection: "column" }}>
                {config.limitedPractice.title}
                <span>{selectedTerm}</span>
              </div>
            ),
            config: config.limitedPractice,
            initialEditValues: selectedLimitedPractice,
            onSubmit: (values) => {
              if (selectedLimitedPractice) {
                handleEdit(
                  "limited-practice-of-profession",
                  selectedLimitedPractice.id,
                  values,
                  setLimitedPracticeData,
                  limitedPracticeData,
                );
              } else {
                handleAdd(
                  "limited-practice-of-profession",
                  {
                    ...values,
                    academic_term: selectedTerm,
                  },
                  setLimitedPracticeData,
                  limitedPracticeData,
                );
              }
            },
          });
        }}
      />

      <TermDetailsSection
        title={config.recipientOrNominee.title}
        fields={config.recipientOrNominee.fields}
        data={selectedRecipientOrNominee}
        selectedTerm={selectedTerm}
        onEdit={() => {
          openModal({
            mode: "edit",
            showDelete: false,
            title: (
              <div style={{ display: "flex", flexDirection: "column" }}>
                {config.recipientOrNominee.title}
                <span>{selectedTerm}</span>
              </div>
            ),
            config: config.recipientOrNominee,
            initialEditValues: selectedRecipientOrNominee,
            onSubmit: (values) => {
              if (selectedRecipientOrNominee) {
                handleEdit(
                  "recipient-or-nominee",
                  selectedRecipientOrNominee.id,
                  values,
                  setRecipientOrNomineeData,
                  recipientOrNomineeData,
                );
              } else {
                handleAdd(
                  "recipient-or-nominee",
                  {
                    ...values,
                    academic_term: selectedTerm,
                  },
                  setRecipientOrNomineeData,
                  recipientOrNomineeData,
                );
              }
            },
          });
        }}
      />

      {/* ========================= */}
      {/* Consultation Hours */}
      {/* ========================= */}

      <ConsultationHoursSection
        columns={config.consultationHours.columns}
        data={sortByDay(consultationHoursData)}
        selectedTerm={selectedTerm}
        onAdd={() =>
          openModal({
            mode: "add",
            showDelete: false,
            config: config.consultationHours,
            onSubmit: (values) =>
              handleAdd(
                "consultation-hours",
                {
                  ...values,
                  academic_term: selectedTerm,
                },
                setConsultationHoursData,
                consultationHoursData,
              ),
            title: (
              <div style={{ display: "flex", flexDirection: "column" }}>
                {config.consultationHours.title}
                <span>{selectedTerm}</span>
              </div>
            ),
          })
        }
        onEdit={(rowData) =>
          openModal({
            mode: "edit",
            showDelete: true,
            title: (
              <div style={{ display: "flex", flexDirection: "column" }}>
                {config.consultationHours.title}
                <span>{selectedTerm}</span>
              </div>
            ),
            config: config.consultationHours,
            initialEditValues: rowData,
            onSubmit: (values) =>
              handleEdit(
                "consultation-hours",
                rowData.id,
                values,
                setConsultationHoursData,
                consultationHoursData,
              ),
            onDelete: () =>
              handleDelete(
                "consultation-hours",
                rowData.id,
                setConsultationHoursData,
                consultationHoursData,
              ),
          })
        }
      />

      {/* ========================= */}
      {/* MODALS */}
      {/* ========================= */}

      <FacultyProfileModal
        open={modalState.open}
        mode={modalState.mode}
        showDelete={modalState.showDelete}
        title={modalState.title}
        config={modalState.config}
        initialEditValues={modalState.initialEditValues}
        onCancel={closeModal}
        onSubmit={(values) => {
          modalState.onSubmit?.(values);
          closeModal();
        }}
        onDelete={() => {
          modalState.onDelete?.();
          closeModal();
        }}
      />

      <GeneratePortfolioModal
        open={portfolioModalState.open}
        onCancel={closePortfolioModal}
        onSubmit={(values) => {
          portfolioModalState.onSubmit?.(values);
        }}
      />
    </div>
  );
}
